// src/services/api/reports.service.ts

import { apiClient, makeApiRequest } from './client';
import { endpoints } from './config';

// Types for analytics dashboard
export interface ProjectStats {
  id: number;
  name: string;
  totalHours: number;
  expectedHours: number;
  completedHours: number;
  overtimeHours: number;
  completionPercentage: number;
  employeeCount: number;
}

export interface EmployeeStats {
  id: number;
  name: string;
  totalHours: number;
  overtimeHours: number;
  projectCount: number;
  department: string;
}

export interface OvertimeStats {
  totalOvertimeHours: number;
  overtimePay: number;
  pendingSupervisorApprovals: number;
  pendingManagerApprovals: number;
  approvedOvertime: number;
  rejectedOvertime: number;
}

export interface DashboardStats {
  projects: ProjectStats[];
  employees: EmployeeStats[];
  overtime: OvertimeStats;
  totalStats: {
    totalProjects: number;
    totalHours: number;
    totalEmployees: number;
    totalOvertimeHours: number;
    totalOvertimePay: number;
  };
}

export interface ProjectReport {
  project: {
    id: number;
    name: string;
    description: string;
    totalTasks: number;
    completedTasks: number;
    totalHours: number;
    overtimeHours: number;
  };
  tasks: Array<{
    id: number;
    name: string;
    status: string;
    totalHours: number;
    overtimeHours: number;
    completionPercentage: number;
    assignedEmployees: string[];
  }>;
}

export interface EmployeeReport {
  employee: {
    id: number;
    name: string;
    department: string;
    totalHours: number;
    totalTasks: number;
    completedTasks: number;
    overtimeHours: number;
  };
  recentTimeEntries: Array<{
    id: number;
    date: string;
    hours: number;
    description: string;
    taskName: string;
  }>;
}

export class ReportsService {
  async getAnalyticsDashboard(): Promise<DashboardStats> {
    return makeApiRequest(() => 
      apiClient.get<DashboardStats>('/reports/analytics')
    );
  }

  async getProjectReport(projectId: number): Promise<ProjectReport> {
    return makeApiRequest(() => 
      apiClient.get<ProjectReport>(endpoints.reports.project(projectId))
    );
  }

  async getEmployeeReport(userId: number): Promise<EmployeeReport> {
    return makeApiRequest(() => 
      apiClient.get<EmployeeReport>(endpoints.reports.employee(userId))
    );
  }

  async getWeeklyReport(startDate: string, endDate: string, projectId?: number, userId?: number): Promise<any> {
    return makeApiRequest(() => 
      apiClient.get<any>(endpoints.reports.weekly, {
        start_date: startDate,
        end_date: endDate,
        project_id: projectId,
        user_id: userId
      })
    );
  }
}

export const reportsService = new ReportsService();